
import { useState } from "react";
import Link from "@/components/ui/link-compat";
import { useRouter, useSearchParams } from "@/lib/router-compat";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSuperAdminUserDetail, useImpersonate } from "@/hooks/useSuperAdmin";
import { formatDate } from "@/lib/utils";
import { Building2, LogIn } from "lucide-react";
import { DetailPageSkeleton } from "@/components/ui/skeletons";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function UserDetailPage() {
  const searchParams = useSearchParams();
  const id = searchParams.get("id") ?? "";
  const { isSuperAdmin } = useAuth();
  const router = useRouter();
  const { data: user, isLoading } = useSuperAdminUserDetail(id);
  usePageTitle(user?.email ?? 'User');
  const impersonate = useImpersonate();
  const [showConfirm, setShowConfirm] = useState(false);

  if (!isSuperAdmin) {
    router.push("/dashboard");
    return null;
  }

  if (isLoading) {
    return <DetailPageSkeleton />;
  }

  if (!user) {
    return <div className="flex items-center justify-center py-10">User not found.</div>;
  }

  const handleImpersonate = async () => {
    const result = await impersonate.mutateAsync(user.id);
    localStorage.setItem('token', result.token);
    localStorage.removeItem('currentOrg');
    window.location.href = '/dashboard';
  };

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");

  return (
    <div className="space-y-6">
      <PageHeader
        title={user.email}
        description={[fullName, `Joined ${formatDate(user.createdAt)}`]
          .filter(Boolean)
          .join(" · ")}
        breadcrumb={[
          { label: "Admin", href: "/dashboard/settings/admin" },
          { label: user.email },
        ]}
        askAi
        hideTitleOnMobile={false}
        actions={
          <>
            {user.isSuperAdmin && <Badge variant="destructive">Super Admin</Badge>}
            {!user.isSuperAdmin && (
              <Button onClick={() => setShowConfirm(true)} variant="outline" className="gap-2" disabled={impersonate.isPending}>
                <LogIn className="h-4 w-4" />
                Login as this user
              </Button>
            )}
          </>
        }
      />

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogMedia className="bg-amber-100 dark:bg-amber-950">
              <LogIn className="size-8 text-amber-600 dark:text-amber-400" />
            </AlertDialogMedia>
            <AlertDialogTitle>Impersonate user?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <p>
                You will be logged in as <strong>{user.email}</strong>. Your current session will end.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleImpersonate}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Organizations table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organizations ({user.organizations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {user.organizations.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No organizations"
              description="This user does not belong to any organization."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {user.organizations.map((org) => (
                  <TableRow key={org.orgId}>
                    <TableCell>
                      <Link href={`/dashboard/settings/admin/organization?id=${org.orgId}`} className="text-primary hover:underline">
                        {org.orgName}
                      </Link>
                    </TableCell>
                    <TableCell><Badge variant={org.role === "Owner" ? "default" : "secondary"}>{org.role}</Badge></TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatDate(org.joinedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/settings/admin/user')({ component: UserDetailPage });
